import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Check, ChevronDown, ChevronUp, FlaskConical, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import { lessonByKey } from '../content'
import {
  FREE_PRACTICE,
  endPractice,
  fireEvent,
  getPracticeSnapshot,
  loadPracticeFixture,
  nextEvent,
  updatePractice,
  type PracticeSession,
} from './engine'
import { usePracticeSession } from './use-practice-session'
import { stepDone, stepHref } from './steps'

/** Practice mode's strip across the top: always visible, so nobody mistakes it for real work. */
export function PracticeBanner() {
  const session = usePracticeSession()
  return session ? <Banner session={session} /> : null
}

function Banner({ session }: { session: PracticeSession }) {
  const finish = useEndPractice()
  return (
    <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-sm text-amber-900 md:px-6">
      <span className="inline-flex min-w-0 items-center gap-2">
        <FlaskConical className="size-4 shrink-0" aria-hidden="true" />
        <span className="truncate">
          <span className="font-semibold">Practice mode.</span>
          <span className="hidden sm:inline"> Nothing you change is saved.</span>
        </span>
      </span>
      <button
        type="button"
        onClick={() => finish(session.lessonKey)}
        className="shrink-0 rounded-md px-2 py-0.5 font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
      >
        End practice
      </button>
    </div>
  )
}

function useEndPractice() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return (lessonKey: string) => {
    endPractice()
    queryClient.removeQueries()
    if (lessonKey === FREE_PRACTICE) void navigate({ to: '/learn' })
    else void navigate({ to: '/learn/$lessonKey', params: { lessonKey } })
  }
}

/** The step-by-step panel for the lesson being practised. */
export function PracticeCoach() {
  const session = usePracticeSession()
  return session ? <Coach session={session} /> : null
}

function Coach({ session }: { session: PracticeSession }) {
  const { pathname } = useLocation()
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const finish = useEndPractice()
  // The step list starts folded, so the panel covers as little of the page as it can.
  const [collapsed, setCollapsed] = useState(true)
  const [eventPending, setEventPending] = useState(false)
  const lesson = lessonByKey(session.lessonKey)
  const scenario = session.scenario

  const fixture = useQuery({
    queryKey: ['learn', 'practice-fixture', scenario],
    enabled: scenario !== null,
    staleTime: Infinity,
    queryFn: async () => {
      const token = await getAccessToken()
      return loadPracticeFixture(scenario ?? '', { Authorization: `Bearer ${token ?? ''}` })
    },
  })

  // A new stage changes what the recorded pages show: reload whatever is on screen.
  const stage = session.stage
  const seenStage = useRef(stage)
  useEffect(() => {
    if (stage !== seenStage.current) {
      seenStage.current = stage
      void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'learn' })
    }
  }, [stage, queryClient])

  const steps = useMemo(() => lesson?.practice?.steps ?? [], [lesson])
  const current = steps[session.step]
  const finished = session.step >= steps.length

  // Move on as soon as the trainee has done what the step asks, or a later step already.
  useEffect(() => {
    if (!current) return
    let done = -1
    for (let i = session.step; i < steps.length; i += 1) {
      const step = steps[i]
      if (step && stepDone(step, session, fixture.data, pathname)) done = i
    }
    if (done >= session.step) updatePractice({ step: done + 1, stepWrites: session.writes })
  }, [session, current, steps, fixture.data, pathname])

  if (!lesson) return null

  const next = () => updatePractice({ step: session.step + 1, stepWrites: session.writes })
  const event = current?.done && 'event' in current.done && fixture.data ? nextEvent(fixture.data, session.stage) : undefined
  const fire = () => {
    if (!fixture.data) return
    const data = fixture.data
    setEventPending(true)
    // A beat for the email to "arrive" before the AI's answer shows up.
    window.setTimeout(() => {
      fireEvent(data)
      updatePractice({ step: (getPracticeSnapshot()?.step ?? session.step) + 1 })
      setEventPending(false)
    }, 1800)
  }
  const href = current ? stepHref(current, fixture.data) : null

  return (
    <aside
      aria-label="Practice steps"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t bg-background shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.25)]',
        'md:inset-x-auto md:bottom-4 md:right-4 md:w-[22rem] md:rounded-xl md:border md:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.3)]',
      )}
    >
      <div className="flex items-center gap-2 px-4 pb-2 pt-3">
        <FlaskConical className="size-4 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1 truncate text-sm font-semibold">{lesson.title}</div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {Math.min(session.step + 1, steps.length)} of {steps.length}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={collapsed ? 'Show steps' : 'Hide steps'}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </Button>
      </div>

      <div className="space-y-3 px-4 pb-4">
        {fixture.isError ? (
          <p className="text-sm text-red-700">This practice couldn’t load. End practice and try again.</p>
        ) : finished ? (
          <div className="space-y-3">
            <p className="text-sm">All done. Take the quiz to finish the lesson.</p>
            <Button size="sm" onClick={() => finish(session.lessonKey)}>
              Back to the lesson
            </Button>
          </div>
        ) : (
          current && (
            <div className="space-y-3">
              <p className="text-[15px] leading-snug">{current.text}</p>
              <div className="flex flex-wrap items-center gap-2">
                {event ? (
                  <LoadingButton
                    size="sm"
                    loading={eventPending}
                    loadingText="Arriving..."
                    onClick={fire}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    {event.event}
                  </LoadingButton>
                ) : (
                  href && (
                    <Button size="sm" variant="outline" asChild>
                      <Link to={href}>
                        {current.linkLabel ?? 'Open'} <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  )
                )}
                {!current.done && (
                  <Button size="sm" variant="ghost" onClick={next}>
                    {session.step === steps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                )}
              </div>
            </div>
          )
        )}

        {!collapsed && (
          <ol className="space-y-1.5 border-t pt-3 text-sm">
            {steps.map((step, i) => (
              <li
                key={step.text}
                className={cn(
                  'flex gap-2',
                  i < session.step && 'text-muted-foreground',
                  i > session.step && 'text-muted-foreground/70',
                  i === session.step && 'font-medium',
                )}
              >
                <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
                  {i < session.step ? (
                    <Check className="size-3.5 text-emerald-600" aria-label="Done" />
                  ) : (
                    <span className="text-xs tabular-nums">{i + 1}</span>
                  )}
                </span>
                <span className="line-clamp-2">{step.text}</span>
              </li>
            ))}
          </ol>
        )}

        <button
          type="button"
          onClick={() => finish(session.lessonKey)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="size-3.5" aria-hidden="true" /> End practice
        </button>
      </div>
    </aside>
  )
}
